import * as types from '../mutation-types';
import AgentAPI from '../../api/agents';
import Vue from 'vue';

// Función para calcular la nueva página después de una eliminación
const calculateNewPage = (currentPage, totalCount, pageSize) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  let newPage = currentPage;

  if (newPage > totalPages) {
    newPage = totalPages; // Ajusta la página si estamos por encima del límite
  }

  // validación para que la página no sea menor a 1
  if (newPage < 1) {
    newPage = 1;
  }

  return newPage;
};

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
  meta: {
    currentPage: 1,
    count: 0,
    pageSize: 15,
  },
  appliedFilters: [],
  sortOrder: [],
};

export const getters = {
  /**
   * Obtiene todos los agentes
   * @param {Object} $state - El estado actual del módulo.
   * @returns {Array} - Un arreglo de agentes
   */
  getAgents($state) {
    return $state.records.filter(record => record !== undefined);
  },
  /**
   * Obtiene todos los agentes en el orden especificado.
   * @param {Object} $state - El estado actual del módulo.
   * @returns {Array} - Un arreglo de agentes en el orden definido por `sortOrder`.
   */
  getAgentsPaginated($state) {
    return $state.sortOrder.map(agentId => $state.records[agentId]);
  },

  /**
   * Obtiene el estado de la interfaz de usuario.
   * @param {Object} $state - El estado actual del módulo.
   * @returns {Object} - El objeto que representa los indicadores de estado de la interfaz de usuario.
   */
  getUIFlags($state) {
    return $state.uiFlags;
  },

  /**
   * Obtiene un agente específico por su ID.
   * @param {Object} $state - El estado actual del módulo.
   * @param {string} id - El ID del agente a obtener.
   * @returns {Object} - El objeto del agente correspondiente al ID, o un objeto vacío si no se encuentra.
   */
  getAgent: $state => id => {
    const agent = $state.records[id];
    return agent || {};
  },

  /**
   * Obtiene los metadatos de la paginación.
   * @param {Object} $state - El estado actual del módulo.
   * @returns {Object} - El objeto que contiene los metadatos de paginación.
   */
  getMeta: $state => {
    return $state.meta;
  },

  /**
   * Obtiene los filtros aplicados a los agentes.
   * @param {Object} _state - El estado actual del módulo.
   * @returns {Array} - Un arreglo de los filtros aplicados.
   */
  getAppliedAgentFilters: _state => {
    return _state.appliedFilters;
  },

  /**
   * Obtiene los agentes verificados.
   * @param {Object} $state - El estado actual del módulo.
   * @returns {Array} - Un arreglo de agentes que están confirmados.
   */
  getVerifiedAgents($state) {
    return Object.values($state.records).filter(record => record.confirmed);
  },

  /**
   * Obtiene el estado de disponibilidad de los agentes.
   * @param {Object} $state - El estado actual del módulo.
   * @returns {Object} - Un objeto que contiene el conteo de agentes según su estado de disponibilidad (online, busy, offline).
   */
  getAgentStatus($state) {
    let status = {
      online: Object.values($state.records).filter(
        agent => agent.availability_status === 'online'
      ).length,
      busy: Object.values($state.records).filter(
        agent => agent.availability_status === 'busy'
      ).length,
      offline: Object.values($state.records).filter(
        agent => agent.availability_status === 'offline'
      ).length,
    };
    return status;
  },
};

export const actions = {
  /**
   * Obtiene una lista de agentes con paginación y ordenación.
   * @param {Object} context - El contexto del módulo que contiene `commit` y `state`.
   * @param {Object} params - Los parámetros para la solicitud.
   * @param {number} [params.page=1] - El número de página a obtener.
   * @param {string} [params.sortAttr] - Atributo por el cual ordenar los agentes.
   * @param {string} [params.label] - Etiqueta para filtrar los agentes.
   * @returns {Promise<void>}
   */
  get: async ({ commit }) => {
    commit(types.default.SET_AGENT_FETCHING_STATUS, true);
    try {
      const response = await AgentAPI.get();
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
      commit(types.default.SET_AGENTS, response.data);
    } catch (error) {
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
    }
  },

  /**
   * Obtiene una lista de agentes paginados.
   * @param {Object} context - El contexto del módulo que contiene `commit`.
   * @param {Object} params - Los parámetros para la solicitud.
   * @param {number} [params.page=1] - El número de página a obtener.
   * @param {string} [params.sortAttr] - Atributo por el cual ordenar los agentes.
   * @param {string} [params.label] - Etiqueta para filtrar los agentes.
   * @returns {Promise<void>}
   */
  getPaginated: async ({ commit }, { page = 1, sortAttr, label }) => {
    commit(types.default.SET_AGENT_FETCHING_STATUS, true);
    try {
      const response = await AgentAPI.getPaginated(page, sortAttr, label); // Asegúrate de que esta API exista
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
      commit(types.default.SET_AGENTS_PAGINATED, response.data.payload); // Usa la mutación específica para agentes paginados
      commit(types.default.SET_AGENT_META, response.data.meta);
    } catch (error) {
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
      throw new Error('Error al obtener agentes paginados');
    }
  },

  /**
   * Busca agentes basados en un término de búsqueda y paginación.
   * @param {Object} context - El contexto del módulo que contiene `commit` y `state`.
   * @param {Object} params - Los parámetros para la búsqueda.
   * @param {string} params.search - El término de búsqueda.
   * @param {number} [params.page=1] - El número de página a obtener.
   * @param {string} [params.sortAttr] - Atributo por el cual ordenar los agentes.
   * @param {string} [params.label] - Etiqueta para filtrar los agentes.
   * @returns {Promise<void>}
   */
  search: async ({ commit }, { search, page = 1, sortAttr, label }) => {
    commit(types.default.SET_AGENT_FETCHING_STATUS, true);
    try {
      const response = await AgentAPI.search(search, page, sortAttr, label);
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
      commit(types.default.SET_AGENTS, response.data.payload);
      commit(types.default.SET_AGENT_META, response.data.meta);
      // Update sortOrder based on the order of agents in the response
      commit(
        types.default.SET_AGENT_SORT_ORDER,
        response.data.payload.map(agent => agent.id)
      );
    } catch (error) {
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
    }
  },

  /**
   * Crea un nuevo agente y actualiza el estado.
   * @param {Object} context - El contexto del módulo que contiene `commit` y `state`.
   * @param {Object} agentInfo - Información del agente a crear.
   * @returns {Promise<void>}
   */
  create: async ({ commit, state }, agentInfo) => {
    commit(types.default.SET_AGENT_CREATING_STATUS, true);
    try {
      const response = await AgentAPI.create(agentInfo);
      commit(types.default.ADD_AGENT, response.data);

      // Increment the count of total agents
      commit(types.default.SET_AGENT_META, {
        ...state.meta,
        count: state.meta.count + 1, // Update the count
      });

      commit(types.default.SET_AGENT_CREATING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_CREATING_STATUS, false);
      throw error;
    }
  },

  /**
   * Actualiza la información de un agente existente.
   * @param {Object} context - El contexto del módulo que contiene `commit`.
   * @param {Object} params - Los parámetros para la actualización.
   * @param {string} params.id - El ID del agente a actualizar.
   * @param {Object} params.agentParams - Los parámetros del agente a actualizar.
   * @returns {Promise<void>}
   */
  update: async ({ commit }, { id, ...agentParams }) => {
    commit(types.default.SET_AGENT_UPDATING_STATUS, true);
    try {
      const response = await AgentAPI.update(id, agentParams);
      commit(types.default.EDIT_AGENT, response.data);
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
      throw error;
    }
  },

  /**
   * Actualiza la presencia de un agente específico.
   * @param {Object} context - El contexto del módulo que contiene `commit`.
   * @param {Object} params - Los parámetros para la actualización de presencia.
   * @param {string} params.id - El ID del agente cuya presencia se actualizará.
   * @param {string} params.availabilityStatus - El nuevo estado de disponibilidad del agente.
   */
  updateSingleAgentPresence: ({ commit }, { id, availabilityStatus }) => {
    commit(types.default.UPDATE_SINGLE_AGENT_PRESENCE, {
      id,
      availabilityStatus,
    });
  },

  /**
   * Actualiza la presencia de múltiples agentes.
   * @param {Object} data - Los datos para la actualización de presencia.
   * @returns {Promise<void>}
   */
  updatePresence: async ({ commit }, data) => {
    commit(types.default.UPDATE_AGENTS_PRESENCE, data);
  },

  /**
   * Elimina un agente por su ID.
   * @param {Object} context - El contexto del módulo que contiene `commit` y `state`.
   * @param {string} agentId - El ID del agente a eliminar.
   * @returns {Promise<void>}
   */
  delete: async ({ commit, state }, agentId) => {
    commit(types.default.SET_AGENT_DELETING_STATUS, true);
    try {
      await AgentAPI.delete(agentId);
      commit(types.default.DELETE_AGENT, agentId);

      // calcula la nueva página después de la eliminación
      const newPage = calculateNewPage(
        state.meta.currentPage,
        state.meta.count - 1,
        state.meta.pageSize
      );

      commit(types.default.SET_AGENT_META, {
        ...state.meta,
        count: state.meta.count - 1, // Update the count
        currentPage: newPage, // Adjust the page
      });

      commit(types.default.SET_AGENT_DELETING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
      throw new Error(error);
    }
  },

  // Elimina el agente recibido a través de un broadcast
  deleteByBroadcast: async ({ commit, state }, agentId) => {
    commit(types.default.SET_AGENT_DELETING_STATUS, true);
    try {
      commit(types.default.DELETE_AGENT, agentId);

      // calcula la nueva página después de la eliminación
      const newPage = calculateNewPage(
        state.meta.currentPage,
        state.meta.count - 1,
        state.meta.pageSize
      );

      commit(types.default.SET_AGENT_META, {
        ...state.meta,
        count: state.meta.count - 1, // Actualiza el conteo
        currentPage: newPage, // Ajusta la página
      });

      commit(types.default.SET_AGENT_DELETING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
      throw new Error(error);
    }
  },

  /**
   * Filtra agentes según los criterios especificados.
   * @param {Object} context - El contexto del módulo que contiene `commit`.
   * @param {Object} params - Los parámetros de filtrado.
   * @param {Object} params.queryPayload - Los criterios de filtrado para la solicitud.
   * @returns {Promise<void>}
   */
  filter: async ({ commit }, { queryPayload }) => {
    commit(types.default.SET_AGENT_FETCHING_STATUS, true);
    try {
      const response = await AgentAPI.filter(queryPayload);
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
      commit(types.default.SET_AGENTS, response.data.payload);
      commit(types.default.SET_AGENT_META, response.data.meta);
      commit(
        types.default.SET_AGENT_SORT_ORDER,
        response.data.payload.map(agent => agent.id)
      );
      commit(
        types.default.SET_APPLIED_AGENT_FILTERS,
        response.data.appliedFilters
      );
    } catch (error) {
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
    }
  },

  /**
   * Limpia los filtros aplicados a los agentes.
   * @param {Object} context - El contexto del módulo que contiene `commit`.
   * @returns {Promise<void>}
   */
  clearAgentFilters: async ({ commit }) => {
    commit(types.default.SET_AGENT_FETCHING_STATUS, true);
    try {
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
      commit(types.default.SET_APPLIED_AGENT_FILTERS, []);
      commit(types.default.SET_AGENT_META, { currentPage: 1, count: 0 });
      commit(types.default.SET_AGENTS, []);
    } catch (error) {
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
    }
  },
};

export const mutations = {
  // Mutations for agent management
  [types.default.SET_AGENT_FETCHING_STATUS]($state, status) {
    $state.uiFlags.isFetching = status;
  },

  [types.default.SET_AGENT_CREATING_STATUS]($state, status) {
    $state.uiFlags.isCreating = status;
  },

  [types.default.SET_AGENT_UPDATING_STATUS]($state, status) {
    $state.uiFlags.isUpdating = status;
  },

  [types.default.SET_AGENT_DELETING_STATUS]($state, status) {
    $state.uiFlags.isDeleting = status;
  },

  // Set agents and manage their records
  [types.default.SET_AGENTS]($state, agents) {
    const sortOrder = agents.map(agent => {
      Vue.set($state.records, agent.id, agent);
      return agent.id;
    });
    $state.sortOrder = sortOrder;
  },

  /**
   * Establece los agentes paginados en el estado.
   *
   * Esta mutación reinicia los registros y los actualiza con los nuevos
   * agentes proporcionados, manteniendo el orden por ID.
   *
   * @param {Object} $state - El objeto de estado actual.
   * @param {Array<Object>} agents - Lista de objetos agente a establecer.
   * @param {number} agents[].id - El identificador único de cada agente.
   * @param {Object} agents[].data - Datos asociados a cada agente.
   *
   * @returns {void} No retorna ningún valor.
   */
  [types.default.SET_AGENTS_PAGINATED]($state, agents) {
    $state.records = [];
    const sortOrder = agents.map(agent => {
      Vue.set($state.records, agent.id, agent);
      return agent.id;
    });
    $state.sortOrder = sortOrder;
  },

  [types.default.ADD_AGENT]($state, agent) {
    Vue.set($state.records, agent.id, agent);
    $state.sortOrder.push(agent.id);
  },

  [types.default.EDIT_AGENT]($state, agent) {
    Vue.set($state.records, agent.id, agent);
  },

  [types.default.DELETE_AGENT]($state, agentId) {
    const index = $state.sortOrder.findIndex(id => id === agentId);
    if (index !== -1) {
      $state.sortOrder.splice(index, 1);
    }
    delete $state.records[agentId];
  },

  // Update presence status for agents
  [types.default.UPDATE_AGENTS_PRESENCE]($state, presence) {
    Object.keys(presence).forEach(agentId => {
      const availability_status = presence[agentId];
      if ($state.records[agentId]) {
        $state.records[agentId].availability_status = availability_status;
      }
    });
  },

  [types.default.UPDATE_SINGLE_AGENT_PRESENCE](
    $state,
    { id, availabilityStatus }
  ) {
    if ($state.records[id]) {
      $state.records[id].availability_status = availabilityStatus;
    }
  },

  // Manage agent metadata and applied filters
  [types.default.SET_AGENT_META]($state, meta) {
    $state.meta = meta;
  },

  [types.default.SET_APPLIED_AGENT_FILTERS]($state, filters) {
    $state.appliedFilters = filters;
  },

  [types.default.SET_AGENT_SORT_ORDER]($state, sortOrder) {
    $state.sortOrder = sortOrder;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
